import { IPushNotificationMessage } from '@AppModels/.';
import { useEventLogging } from '@AppModels/Logging';
import { canSendHtmlNewsletters, canViewContentCalendarAndCampaigns } from '@AppModels/UiUtils';
import * as Api from '@ViewModels';
import { ActionItemComposerModal } from '@WebComponents/actionItems/ActionItemComposer';
import { SingleEmailComposerModal } from '@WebComponents/email/SingleEmailComposerModal';
import { NoteComposerModal } from '@WebComponents/notes/NoteComposer';
import { PushNotifications } from '@WebComponents/PushNotifications';
import { QuickAddEntity } from '@WebComponents/QuickAddEntity';
import { EmailReadOrOpenedToast } from '@WebComponents/Toaster/EmailReadOrOpenedToast';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Redirect, Switch, useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import {
	useEnvironment,
	usePushNotifications,
	useQuickAddEntity,
	useTextMessaging,
	useUserSession,
} from '../../../models/hooks/appStateHooks';
import { KnownCategories, UserSessionContext, VmUtils } from '../../../viewmodels/AppViewModels';
import { BrowserPushNotificationsViewModel } from '../../../viewmodels/PushNotifications';
import { AppLayout } from '../../components-v2/AppLayout/AppLayout';
import { Dialog, DialogPopup } from '../../components-v2/Dialog';
import { EnablePushNotificationsModal } from '../../components/EnablePushNotificationsModal';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { WebFullscreenModal } from '../../components/fullscreen/WebFullscreenModal';
import { IRouterLocation, PrivateRoute } from '../../components/PrivateRoute';
import { ROUTES } from '../../constants/routes';
import { AutomationFlowEditorPage, AutomationFlowRedirect } from '../../pages/AutomationFlowEditor';
import { Contacts } from '../../pages/Contacts';
import { Calendar } from '../../pages/Calendar';
import { DataBoardsRoute } from '../../pages/DataBoards';
import { ManagedAccountsPage } from '../../pages/ManagedAccounts';
import { LazyDocumentsRoute } from '../../pages/Documents/lazy';
import { LazySurveysRoute } from '../../pages/Surveys/lazy';
import { isLevitateHostedSite } from '../../../extViewmodels/sdk/models';
import { WebsitePage } from '../Website';
import { AIResolver } from '../AIResolver';
import { AppLinks } from '../AppLinks';
import { Automations } from '../Automations';
import { BuildInfo } from '../BuildInfo';
import { Campaigns } from '../Campaigns';
import { Company } from '../Company';
import { Contact } from '../Contact';
import { ContactsImportFromCSVPage } from '../ContactsImportFromCSVPage';
import { CustomerContentCalendarPage } from '../ContentCalendar/CustomerContentCalendarPage';
import { CreateAutomationTemplatePage } from '../CreateAutomationTemplate';
import { EditHtmlNewsletterTemplate } from '../EditHtmlNewsletterTemplate';
import { GenericKeyDates } from '../GenericKeyDates';
import { Dashboard } from '../Happiness/Dashboard';
import { Integrations } from '../integrations/Integrations';
import { KeepInTouches } from '../KeepInTouches';
import { LazyLoader } from '../LazyLoader';
import { Logout } from '../Logout';
import { NoteActionItemsContainer } from '../NoteActionItemsContainer';
import { NotFoundContainer } from '../NotFoundContainer';
import { RecentlyViewedEmails } from '../RecentlyViewedEmails';
import { RecentMeetings } from '../RecentMeetings';
import { ScheduleMeetingSettingsPage } from '../ScheduleMeeting/ScheduleMeetingSettingsPage';
import { Settings } from '../Settings';
import { ManageUserGroups } from '../Settings/components/ManageUsersGroups';
import { ManageUsers } from '../Settings/ManageUsers';
import { TeamMeetingLinksPage } from '../TeamMeetingLinks';
import { TestimonialImageEditorContainer } from '../TestimonialImageEditor';
import { Texting } from '../Texting';
import { TextingRegistrationFormPage } from '../TextingRegistration/TextingRegistrationForm';
import { TextingRegistrationOptIn } from '../TextingRegistration/TextingRegistrationOptIn';
import './styles.less';

const LazyRegistrationRoute = React.lazy(() => import('../TextingRegistration'));
const LazyReportingRoute = React.lazy(() => import('../reporting'));
const LazyVolunteersRoute = React.lazy(() => import('../../pages/Volunteers'));
const LazyExportDownloadPage = React.lazy(() =>
	import('../../pages/ExportDownload').then(module => ({
		default: module.ExportDownloadPage,
	}))
);
const LazyEmailExecBriefsPage = React.lazy(() =>
	import('../../pages/EmailExecBriefs').then(module => ({
		default: module.EmailExecBriefsPage,
	}))
);
const LazyMeetingPrepBriefsPage = React.lazy(() =>
	import('../../pages/MeetingPrepBriefs').then(module => ({
		default: module.MeetingPrepBriefsPage,
	}))
);

export interface INavigationItemProps {
	routeContainerClassName?: string;
	showingCompactNav?: boolean;
}

function _MainContainer() {
	const history = useHistory();
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('MainContainer');
	const environment = useEnvironment();
	const quickAddEntity = useQuickAddEntity();
	const pushNotifications = usePushNotifications();
	const textMessaging = useTextMessaging();

	const onEnablePushNotificationsModalRequestClose = async (enablePushNotifications?: boolean, cancel?: boolean) => {
		const logError = (error: any) => {
			const err = Api.asApiError(error);
			logApiError(`${enablePushNotifications ? 'Enable' : 'Disable'}BrowserPush-Error`, err);
		};
		if (cancel) {
			return pushNotifications.denyPushNotifications();
		}
		const resolvedValue = !!enablePushNotifications;
		await pushNotifications.toggleNotifications(resolvedValue).catch(logError);
	};

	const onRenderModals = () => {
		const showPushNotificationsPrompt = !!pushNotifications?.shouldShowPromptDialog;
		return (
			<React.Fragment key='main-container-modals'>
				<WebFullscreenModal />
				<ActionItemComposerModal />
				<NoteComposerModal />
				{environment.appType !== 'admin' && <SingleEmailComposerModal />}
				<Dialog
					open={quickAddEntity.isOpen}
					onOpenChange={open => {
						if (!open) {
							quickAddEntity.close();
						}
					}}
				>
					<DialogPopup>
						<QuickAddEntity
							quickAddEntityVm={quickAddEntity}
							onContactCreated={contact => history.push(`${ROUTES.contacts.people}/${contact.id}`)}
						/>
					</DialogPopup>
				</Dialog>
				{!!BrowserPushNotificationsViewModel.isSupported && (
					<EnablePushNotificationsModal
						modalProps={{
							isOpen: !!showPushNotificationsPrompt,
							onRequestClose: onEnablePushNotificationsModalRequestClose,
						}}
					/>
				)}
			</React.Fragment>
		);
	};

	const onPushNotificationMessageReceived = (e: MessageEvent) => {
		const pushNotificationMessage: IPushNotificationMessage = e.data;
		if (!!pushNotificationMessage && !!pushNotificationMessage.message && !!pushNotificationMessage.title) {
			toast(<EmailReadOrOpenedToast pushMessage={pushNotificationMessage} />, {
				duration: 5000,
			});
		}
	};

	/** Enforce permissions, handle special cases, etc. */
	const onRouteLoad = (routerLocation: IRouterLocation, _userSession: UserSessionContext) => {
		const lowerPathName = routerLocation.pathname?.toLocaleLowerCase();
		if (_userSession?.userRole === 'limitedUser') {
			switch (lowerPathName) {
				case '/companies': {
					return '/dashboard';
				}
				case '/automations': {
					return '/dashboard';
				}
				default: {
					break;
				}
			}
		}
	};

	return (
		<ErrorBoundary>
			<Switch>
				<PrivateRoute
					path='/launch'
					pageViewLogName={location.pathname}
					render={props => (
						<AppLayout>
							<AppLinks {...props} />
						</AppLayout>
					)}
					userSession={userSession}
				/>
				<PrivateRoute path='/build-info' pageViewLogName='build-info' userSession={userSession}>
					<AppLayout>
						<BuildInfo />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute
					path='/logout'
					pageViewLogName='logout'
					render={props => (
						<AppLayout>
							<Logout {...props} userSession={userSession} textMessaging={textMessaging} />
						</AppLayout>
					)}
					userSession={userSession}
				/>
				<PrivateRoute
					path='/dashboard/recently-viewed-emails'
					pageViewLogName='dashboard/recently-viewed-emails'
					userSession={userSession}
				>
					<AppLayout>
						<RecentlyViewedEmails />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute
					path='/dashboard/recent-meetings'
					pageViewLogName='dashboard/recent-meetings'
					userSession={userSession}
				>
					<AppLayout>
						<RecentMeetings />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute
					path='/dashboard/keep-in-touches'
					pageViewLogName='dashboard/keep-in-touches'
					userSession={userSession}
				>
					<AppLayout>
						<KeepInTouches />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute
					path='/dashboard/upcoming-key-dates'
					pageViewLogName='dashboard/upcoming-key-dates'
					userSession={userSession}
				>
					<AppLayout>
						<GenericKeyDates />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/dashboard' pageViewLogName='dashboard' userSession={userSession}>
					<AppLayout>
						<Dashboard />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/content-calendar' pageViewLogName='content-calendar' userSession={userSession}>
					<AppLayout>
						<CustomerContentCalendarPage />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path={ROUTES.calendar} pageViewLogName='calendar' userSession={userSession}>
					<AppLayout>
						<Calendar />
					</AppLayout>
				</PrivateRoute>
				{userSession?.account?.features?.aida?.enabled && (
					<PrivateRoute path='/templates' userSession={userSession}>
						<AppLayout>
							<Redirect
								exact={true}
								from='/templates'
								to={`/campaigns/email?category=${encodeURIComponent(KnownCategories.MyTemplates)}`}
							/>
						</AppLayout>
					</PrivateRoute>
				)}
				{canSendHtmlNewsletters(userSession) ? (
					<PrivateRoute path='/campaigns/htmlnewsletter/:id' userSession={userSession}>
						<AppLayout>
							<EditHtmlNewsletterTemplate />
						</AppLayout>
					</PrivateRoute>
				) : null}
				{canSendHtmlNewsletters(userSession) ? (
					<Redirect exact={true} from='/campaigns/htmlnewsletter' to={`/campaigns/newsletters`} />
				) : null}
				{canViewContentCalendarAndCampaigns(userSession) && (
					<PrivateRoute path='/campaigns/:tab?' userSession={userSession}>
						<AppLayout>
							<Campaigns routeContainerClassName='main-container-child' />
						</AppLayout>
					</PrivateRoute>
				)}
				<PrivateRoute path={`${ROUTES.contacts.people}/import`} userSession={userSession}>
					<AppLayout>
						<ContactsImportFromCSVPage />
					</AppLayout>
				</PrivateRoute>
				<Redirect from='/people/:id' to={`${ROUTES.contacts.people}/:id`} />
				<Redirect from='/companies/:id' to={`${ROUTES.contacts.companies}/:id`} />
				<PrivateRoute path={`${ROUTES.contacts.people}/:id`} pageViewLogName='contact/view' userSession={userSession}>
					<AppLayout>
						<Contact />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute
					path={`${ROUTES.contacts.companies}/:id`}
					pageViewLogName='company/view'
					userSession={userSession}
				>
					<AppLayout>
						<Company />
					</AppLayout>
				</PrivateRoute>
				<Redirect from='/people' to={ROUTES.contacts.people} />
				<Redirect from='/companies' to={ROUTES.contacts.companies} />
				<PrivateRoute path={ROUTES.contacts.main} pageViewLogName='people' userSession={userSession}>
					<AppLayout>
						<Contacts />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/notes-action-items/:id?' pageViewLogName='notes-action-items' userSession={userSession}>
					<AppLayout>
						<NoteActionItemsContainer />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute
					path='/texting/registration/form'
					pageViewLogName='campaign-registration-form'
					userSession={userSession}
					onLoad={onRouteLoad}
				>
					<AppLayout>
						<TextingRegistrationFormPage />
					</AppLayout>
				</PrivateRoute>
				{userSession?.account?.features?.texting?.enabled ? (
					<PrivateRoute
						path='/texting/registration/opt-in'
						pageViewLogName='campaign-registration-opt-in'
						userSession={userSession}
						onLoad={onRouteLoad}
					>
						<AppLayout>
							<TextingRegistrationOptIn />
						</AppLayout>
					</PrivateRoute>
				) : null}

				<PrivateRoute path='/texting/registration' pageViewLogName='campaign-registration' userSession={userSession}>
					<AppLayout>
						<LazyLoader>
							<LazyRegistrationRoute />
						</LazyLoader>
					</AppLayout>
				</PrivateRoute>

				<PrivateRoute path={ROUTES.reporting.home} userSession={userSession}>
					<AppLayout>
						<LazyLoader>
							<LazyReportingRoute />
						</LazyLoader>
					</AppLayout>
				</PrivateRoute>

				<Redirect from='/settings/schedule-meeting' to='/schedule-meeting' />
				<PrivateRoute
					path='/schedule-meeting/team-links'
					pageViewLogName='settings/schedule-meeting/team-links'
					userSession={userSession}
				>
					<AppLayout>
						<TeamMeetingLinksPage />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/schedule-meeting' pageViewLogName='settings/schedule-meeting' userSession={userSession}>
					<AppLayout>
						<ScheduleMeetingSettingsPage />
					</AppLayout>
				</PrivateRoute>
				<Redirect from='/settings/manage-users' to='/settings/admin/manage-users' />
				<PrivateRoute path='/settings/admin/manage-users' userSession={userSession}>
					<AppLayout>
						<ManageUsers />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/settings/admin/manage-groups' userSession={userSession}>
					<AppLayout>
						<ManageUserGroups />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/settings/:category?/:action?' pageViewLogName='settings' userSession={userSession}>
					<AppLayout>
						<Settings />
					</AppLayout>
				</PrivateRoute>
				<Redirect exact={true} from='/boards/opportunities' to='/dataBoards/opportunities' />

				<PrivateRoute path='/dataBoards' userSession={userSession}>
					<AppLayout>
						<LazyLoader>
							<DataBoardsRoute />
						</LazyLoader>
					</AppLayout>
				</PrivateRoute>

				<PrivateRoute path={ROUTES.volunteers.home} userSession={userSession}>
					<AppLayout>
						<LazyLoader>
							<LazyVolunteersRoute />
						</LazyLoader>
					</AppLayout>
				</PrivateRoute>

				<PrivateRoute path='/integrations' pageViewLogName='integrations' userSession={userSession}>
					<AppLayout>
						<Integrations />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/managed-accounts' pageViewLogName='managed-accounts' userSession={userSession}>
					<AppLayout>
						<ManagedAccountsPage />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute
					path='/testimonial-image-editor/:id?'
					pageViewLogName='testimonial-image-editor'
					userSession={userSession}
				>
					<AppLayout>
						<TestimonialImageEditorContainer />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/automations/new' pageViewLogName='create-automation-template' userSession={userSession}>
					<AppLayout>
						<CreateAutomationTemplatePage />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute path='/automations/:id/canvas' pageViewLogName='automation-canvas' userSession={userSession}>
					<AppLayout>
						<AutomationFlowEditorPage />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute exact={true} path='/automations/:id' userSession={userSession}>
					<AutomationFlowRedirect />
				</PrivateRoute>
				<PrivateRoute path='/automations' pageViewLogName='automations' userSession={userSession}>
					<AppLayout>
						<Automations />
					</AppLayout>
				</PrivateRoute>
				{userSession.account.features?.signing?.enabled ? (
					<PrivateRoute path='/documents' pageViewLogName='documents' userSession={userSession}>
						<AppLayout>
							<LazyDocumentsRoute />
						</AppLayout>
					</PrivateRoute>
				) : null}
				{userSession.account.features?.surveys?.enabled ? (
					<PrivateRoute path='/surveys' pageViewLogName='surveys' userSession={userSession}>
						<AppLayout>
							<LazySurveysRoute />
						</AppLayout>
					</PrivateRoute>
				) : (
					<Redirect from='/surveys' to='/dashboard' />
				)}
				<PrivateRoute
					path={`${ROUTES.exports.download}/:jobId`}
					pageViewLogName='export-download'
					userSession={userSession}
				>
					<AppLayout>
						<LazyLoader>
							<LazyExportDownloadPage />
						</LazyLoader>
					</AppLayout>
				</PrivateRoute>
				{isLevitateHostedSite(userSession.account.features?.websites) &&
				userSession.featurePermissions?.website?.read ? (
					<PrivateRoute path='/website' pageViewLogName='website' userSession={userSession}>
						<AppLayout>
							<WebsitePage />
						</AppLayout>
					</PrivateRoute>
				) : (
					<Redirect from='/website' to='/dashboard' />
				)}
				{/* Gated on enabled, not available, so past briefs stay readable while Email Exec is unavailable. */}
				{userSession.account.features?.emailExec?.enabled ? (
					<PrivateRoute
						path={`${ROUTES.emailExec.briefs}/:briefId?`}
						pageViewLogName='email-exec-briefs'
						userSession={userSession}
					>
						<AppLayout>
							<LazyLoader>
								<LazyEmailExecBriefsPage />
							</LazyLoader>
						</AppLayout>
					</PrivateRoute>
				) : null}
				{userSession.account.features?.meetingPrep?.enabled ? (
					<PrivateRoute
						path={`${ROUTES.meetingPrep.briefs}/:briefId?`}
						pageViewLogName='meeting-prep-briefs'
						userSession={userSession}
					>
						<AppLayout>
							<LazyLoader>
								<LazyMeetingPrepBriefsPage />
							</LazyLoader>
						</AppLayout>
					</PrivateRoute>
				) : null}
				{VmUtils.canViewTexting(userSession) ? (
					<PrivateRoute path='/texting' pageViewLogName='texting' exact={true} userSession={userSession}>
						<AppLayout>
							<Texting />
						</AppLayout>
					</PrivateRoute>
				) : null}
				{VmUtils.canViewTexting(userSession) ? (
					<PrivateRoute
						path='/texting/:phoneNumberId/:conversationId?'
						pageViewLogName='texting'
						userSession={userSession}
					>
						<AppLayout>
							<Texting />
						</AppLayout>
					</PrivateRoute>
				) : null}
				<PrivateRoute
					path='/airesolver/:entity/:id/:parentEntity?/:parentId?'
					pageViewLogName='airesolver'
					userSession={userSession}
				>
					<AppLayout>
						<AIResolver />
					</AppLayout>
				</PrivateRoute>
				<PrivateRoute userSession={userSession}>
					<AppLayout>
						<NotFoundContainer />
					</AppLayout>
				</PrivateRoute>
			</Switch>
			{onRenderModals()}
			<PushNotifications onMessage={onPushNotificationMessageReceived} />
		</ErrorBoundary>
	);
}

export const MainContainer = observer(_MainContainer);
